import { useEffect, useState } from "react";
import Modal from "react-modal";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwBadge from "../../../shared/OwBadge/OwBadge";
import { LoadingResults } from "../../../shared/LoadingResults/LoadingResults";
import { useAppContextNonNull } from "../../../../contexts/AppContext";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import ISupplier_Delivery_Schedule from "../../../../models/ISupplier_Delivery_Schedule";
import DeliveryDeadlinesService from "../../../../services/DeliveryDeadlinesService";
import DeliveryScheduleEditor from "./DeliveryScheduleEditor";
import DeliveryScheduleOverridesSection from "./DeliveryScheduleOverridesSection";
import { useIntl } from "react-intl";
import { DAY_ABBR_KEYS, sortDays } from "./deliveryDeadlineUtils";
import "./DeliveryDeadlinesSettings.scss";

/**
 * Supplier-level Delivery Deadlines settings page (route: /business/settings/delivery-deadlines).
 * Lists schedule templates and lets the supplier create/rename/activate/delete them and manage
 * each template's delivery days. Gated by the has_delivery_deadlines supplier flag.
 */
export const DeliveryDeadlinesSettings = () => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    const [schedules, setSchedules] = useState<ISupplier_Delivery_Schedule[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedScheduleGuid, setSelectedScheduleGuid] = useState<string | null>(null);

    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState("");
    const [isCreating, setIsCreating] = useState(false);

    const [pendingDelete, setPendingDelete] = useState<ISupplier_Delivery_Schedule | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [isTriggering, setIsTriggering] = useState(false);

    const loadSchedules = async () => {
        setIsLoading(true);
        try {
            const data = await DeliveryDeadlinesService.getDeliverySchedules(userModel, supplierModel);
            setSchedules(data.deliverySchedules);
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (supplierModel.has_delivery_deadlines) {
            loadSchedules();
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createTemplate = async () => {
        if (!newName.trim()) return;
        setIsCreating(true);
        try {
            const data = await DeliveryDeadlinesService.createDeliverySchedule({ name: newName.trim(), active: true }, userModel, supplierModel);
            uiContext.showNotification({ message: "delivery_schedule_created", level: NotificationLevelEnum.success });
            setNewName("");
            setShowCreate(false);
            await loadSchedules();
            setSelectedScheduleGuid(data.deliverySchedule.guid);
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsCreating(false);
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setIsDeleting(true);
        try {
            await DeliveryDeadlinesService.deleteDeliverySchedule(pendingDelete.guid, userModel, supplierModel);
            uiContext.showNotification({ message: "delivery_schedule_deleted", level: NotificationLevelEnum.success });
            if (selectedScheduleGuid === pendingDelete.guid) {
                setSelectedScheduleGuid(null);
            }
            setPendingDelete(null);
            await loadSchedules();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsDeleting(false);
        }
    };

    const triggerReminders = async () => {
        setIsTriggering(true);
        try {
            const data = await DeliveryDeadlinesService.triggerDeliveryDeadlineReminders(userModel, supplierModel);
            uiContext.showNotification({
                message: data.message || "delivery_deadline_reminders_triggered",
                level: NotificationLevelEnum.success,
            });
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsTriggering(false);
        }
    };

    const daysSummary = (schedule: ISupplier_Delivery_Schedule): string => {
        const days = sortDays(schedule.Days);
        if (!days.length) return intl.formatMessage({ id: "none", defaultMessage: "None" });
        return days.map((d) => intl.formatMessage({ id: DAY_ABBR_KEYS[d.delivery_day_of_week] })).join(", ");
    };

    if (!supplierModel.has_delivery_deadlines) {
        return (
            <div className="DeliveryDeadlinesSettings m-4">
                <OwTitle title="delivery_deadlines" leftIconName="fad fa-truck-clock" />
                <OwLabel title="delivery_deadlines_not_enabled" className="text-muted mt-3" />
            </div>
        );
    }

    return (
        <div className="DeliveryDeadlinesSettings m-4">
            <div className="d-flex justify-content-between align-items-center">
                <OwTitle title="delivery_deadlines" leftIconName="fad fa-truck-clock" />
                <div className="d-flex gap-2">
                    {uiContext.showHiddenFeatures && (
                        <OwButton
                            title="trigger_delivery_deadline_reminders"
                            leftIconClass="fas fa-paper-plane"
                            fn={triggerReminders}
                            className="btn-outline-secondary"
                            disabled={isTriggering}
                        />
                    )}
                    <OwButton title="new_template" leftIconClass="fas fa-plus" fn={() => setShowCreate(true)} className="btn-primary" disabled={showCreate} />
                </div>
            </div>
            <OwLabel title="help_text_delivery_deadlines" className="text-muted" />

            {showCreate && (
                <div className="row g-2 align-items-end mt-2">
                    <div className="col-md-6">
                        <OwLabel title="template_name" noBottomMargin={true} />
                        <input
                            type="text"
                            aria-label="new template name"
                            className="form-control form-control-sm"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="col-md-auto">
                        <OwButton title="create" fn={createTemplate} className="btn-primary btn-sm" disabled={isCreating || !newName.trim()} />
                    </div>
                    <div className="col-md-auto">
                        <OwButton
                            title="cancel"
                            fn={() => {
                                setShowCreate(false);
                                setNewName("");
                            }}
                            className="btn-secondary btn-sm"
                            disabled={isCreating}
                        />
                    </div>
                </div>
            )}

            {isLoading ? (
                <LoadingResults />
            ) : schedules.length === 0 ? (
                <OwLabel title="no_delivery_schedules_yet" className="text-muted mt-4 d-block" />
            ) : (
                <div className="table-responsive mt-3">
                    <table className="table align-middle">
                        <thead>
                            <tr>
                                <th>
                                    <OwLabel title="name" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="status" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="delivery_days" noBottomMargin={true} />
                                </th>
                                <th className="text-end">
                                    <OwLabel title="actions" noBottomMargin={true} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {schedules.map((schedule) => (
                                <tr key={schedule.id} className={selectedScheduleGuid === schedule.guid ? "dd-selected" : ""}>
                                    <td>{schedule.name}</td>
                                    <td>
                                        {schedule.active ? (
                                            <OwBadge title="active" bgColor="bg-success" />
                                        ) : (
                                            <OwBadge title="inactive" bgColor="bg-secondary" />
                                        )}
                                    </td>
                                    <td>{daysSummary(schedule)}</td>
                                    <td className="text-end">
                                        <OwButton
                                            title="manage"
                                            leftIconClass="fas fa-calendar-edit"
                                            fn={() => setSelectedScheduleGuid(schedule.guid)}
                                            className="btn-outline-primary btn-sm me-2"
                                        />
                                        <OwButton
                                            centerIconClass="fas fa-trash"
                                            fn={() => setPendingDelete(schedule)}
                                            className="btn-outline-danger btn-sm"
                                            ariaLabel="delete template"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedScheduleGuid != null && (
                <DeliveryScheduleEditor
                    scheduleGuid={selectedScheduleGuid}
                    onChanged={loadSchedules}
                    onClose={() => setSelectedScheduleGuid(null)}
                />
            )}

            {!isLoading && <DeliveryScheduleOverridesSection schedules={schedules} />}

            <Modal
                isOpen={!!pendingDelete}
                className="modalContentClass"
                overlayClassName="modalOverlayClass"
                contentLabel="confirm delete template"
                shouldCloseOnOverlayClick={!isDeleting}
                onRequestClose={() => !isDeleting && setPendingDelete(null)}>
                <div className="p-3">
                    <OwTitle title="delete_template" tag="h5" leftIconName="fad fa-exclamation-triangle" />
                    <OwLabel title="delete_template_warning" className="mt-2 d-block" />
                    {pendingDelete && <div className="fw-bold mt-2">{pendingDelete.name}</div>}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <OwButton title="cancel" fn={() => setPendingDelete(null)} className="btn-secondary" disabled={isDeleting} />
                        <OwButton title="delete" fn={confirmDelete} className="btn-danger" disabled={isDeleting} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DeliveryDeadlinesSettings;
